import { useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General enquiry", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      toast.success("Message received. We'll reply within one working day.");
      setForm({ name: "", email: "", subject: "General enquiry", message: "" });
    } catch {
      toast.error("Could not send right now — please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full rounded-xl border border-white/15 bg-obsidian px-5 py-4 text-base text-ivory placeholder:text-ivory/30 outline-none transition-colors focus:border-amber";

  return (
    <main data-testid="contact-page" className="min-h-screen bg-void pt-28 sm:pt-32">
      <Seo
        title="Contact — Weird O'Clock"
        siteName="Weird O'Clock"
        description="Questions about an order, a product, or wholesale? Talk to the Weird O'Clock humans."
      />
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-5 pb-24 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>Talk to us</Eyebrow>
          <h1 className="font-display text-4xl font-black tracking-tight text-ivory sm:text-5xl lg:text-6xl">
            Say something{" "}
            <span className="font-editorial italic text-amber">weird</span>
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-ivory/60">
            Order questions, product curiosities, wholesale enquiries, or just a
            photo of your clock in its new home — we read everything and reply
            within one working day.
          </p>
          <div className="mt-10 space-y-4 border-t border-white/10 pt-8">
            <div>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-amber">Response time</p>
              <p className="mt-1 text-sm text-ivory/60">Within 24 hours, Monday to Friday</p>
            </div>
            <div>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-amber">Orders & shipping</p>
              <p className="mt-1 text-sm text-ivory/60">
                Include your order number for the fastest help
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <input
              data-testid="contact-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className={field}
            />
            <input
              data-testid="contact-email-input"
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="your@email.com"
              className={field}
            />
            <select
              data-testid="contact-subject-select"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className={field}
            >
              <option>General enquiry</option>
              <option>Order question</option>
              <option>Shipping & delivery</option>
              <option>Returns & exchanges</option>
              <option>Wholesale</option>
            </select>
            <textarea
              data-testid="contact-message-input"
              required
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us everything…"
              className={field}
            />
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-amber py-4 font-mono2 text-xs font-bold uppercase tracking-[0.2em] text-void transition-all hover:shadow-[0_0_40px_rgba(255,92,0,0.35)] disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Send it <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </main>
  );
}
